import { useEffect, useState } from "react";
import { useCurrentRapidData } from "@rapid/store";
import { useDatabase } from "@rapid/providers/database/DatabaseProvider";
import { useDatasetByRapidDataId } from "@rapid/queries/datasetQueries";

/**
 * Hook to watch the dataset image count in real-time using Firestore listeners.
 *
 * Resolves the dataset ID from the current rapidDataId (via react-query),
 * then subscribes to the dataset document for live `images` field updates.
 */
export function useDatasetImageCount() {
    const currentRapidData = useCurrentRapidData();
    const rapidDataId = currentRapidData?.id;
    const [count, setCount] = useState<number | null>(null);
    const database = useDatabase();

    const { data: dataset } = useDatasetByRapidDataId(rapidDataId);
    const datasetId = dataset?.id ?? null;

    // Subscribe to dataset document for live image count
    useEffect(() => {
        if (!datasetId) {
            setCount(null);
            return;
        }

        const unsub = database.onSnapshot(
            "datasets",
            datasetId,
            (snap) => {
                if (!snap.exists()) {
                    setCount(0);
                } else {
                    setCount(snap.data().images ?? 0);
                }
            },
            (error) => {
                console.warn("[useDatasetImageCount] Listener error:", error);
            }
        );

        return () => {
            unsub();
        };
    }, [datasetId, database]);

    return { count, datasetId };
}
